// Plain messages for the problems of the Assessments area, shared by the list, the documents, the new assessment and the assessment page (ADR-0031: no uids or setting names): the AI service switched off for confidential documents (worded for Admins, who switch it on in Settings, and for everyone else), a document not read yet or used by an assessment, a grid-code version still being built, a full job queue, no job launcher, a stale topic (412), an approved, unfinished or running assessment and incomplete reviews; a job already active and the cost limit of one run are worded for where they happen (reading a document or starting an assessment, or re-running one); the reason a fresh upload was stored without being read; anything else falls back to the shared problem copy, and on the assessment page to "Not found" or "Something went wrong".
import type { Problem } from "@/api/__generated__";
import { isApiProblem, problemCopy, type ProblemCopy } from "@/api/problem";

export type ProblemPlace = "documents" | "assessment";

export const AI_SERVICE_OFF: ProblemCopy = {
  title: "The AI service is switched off for confidential documents",
  description:
    "An administrator must switch it on once IT has approved the service for confidential documents. Until then documents are stored but not read, and assessments cannot start.",
};

export const AI_SERVICE_OFF_FOR_ADMINS: ProblemCopy = {
  title: AI_SERVICE_OFF.title,
  description:
    "Switch it on in Settings once IT has approved the service for confidential documents. Until then documents are stored but not read, and assessments cannot start.",
};

export function isAiServiceOff(copy: ProblemCopy | null): boolean {
  return copy?.title === AI_SERVICE_OFF.title;
}

export const OVER_CAP: ProblemCopy = {
  title: "Above the cost limit of one run",
  description: "Include fewer product types, or ask an administrator to raise the limit.",
};

const COPY: Record<string, ProblemCopy> = {
  "confidential-llm-disabled": AI_SERVICE_OFF,
  "capability-document-not-ready": {
    title: "The document is not read yet",
    description: "Wait until reading has finished, then start the assessment.",
  },
  "capability-document-in-use": {
    title: "An assessment uses this document",
    description:
      "Its entries stay as they are while an assessment uses them. Delete those assessments first.",
  },
  "version-not-ready": {
    title: "This grid-code version is still being built",
    description: "Choose a version that is released.",
  },
  "job-queue-full": {
    title: "Too many jobs are waiting",
    description: "Try again in a few minutes.",
  },
  "job-launcher-unavailable": {
    title: "Jobs cannot start on this server",
    description: "No job launcher is available. Ask an administrator.",
  },
  "precondition-failed": {
    title: "Someone else reviewed this topic",
    description: "The latest version is shown now. Check it and review the topic again.",
  },
  "assessment-approved": {
    title: "This assessment is approved",
    description: "Its results can't change. An approver can reopen it first.",
  },
  "assessment-state-conflict": {
    title: "This can't be done in the current state",
    description:
      "A changed topic is approved by keeping the change, or reset to the engine result first. An approved assessment needs to be reopened.",
  },
  "review-incomplete": {
    title: "Some topics still wait for review",
    description: "Review every topic before approving the assessment.",
  },
  "assessment-not-ready": {
    title: "The assessment is not finished",
    description: "Only a finished assessment whose topics are all reviewed can be approved.",
  },
  "assessment-running": {
    title: "The assessment is running",
    description: "Wait for the run to finish, or ask an administrator to cancel it, before deleting it.",
  },
};

const PLACE_COPY: Record<ProblemPlace, Record<string, ProblemCopy>> = {
  documents: {
    "job-already-active": {
      title: "The document is being read right now",
      description: "Wait until reading has finished, then try again.",
    },
    "budget-exceeded": OVER_CAP,
  },
  assessment: {
    "job-already-active": {
      title: "A run is already in progress",
      description: "Wait for it to finish before starting another one.",
    },
    "budget-exceeded": {
      title: OVER_CAP.title,
      description: "Re-run fewer topics, or ask an administrator to raise the limit.",
    },
  },
};

const NOT_FOUND: ProblemCopy = {
  title: "Not found",
  description: "It may have been deleted, or you can't open its grid code.",
};

const UNKNOWN: ProblemCopy = { title: "Something went wrong", description: "Try again." };

export function problemCodeOf(error: unknown): string | null {
  if (isApiProblem(error)) {
    return error.code;
  }
  if (error !== null && typeof error === "object" && "code" in error && typeof error.code === "string") {
    return error.code;
  }
  return null;
}

function knownCopy(code: string | null, place: ProblemPlace): ProblemCopy | undefined {
  return code === null ? undefined : (PLACE_COPY[place][code] ?? COPY[code]);
}

export function assessmentProblemCopy(error: unknown, place: ProblemPlace = "documents"): ProblemCopy | null {
  const known = knownCopy(problemCodeOf(error), place);
  if (known !== undefined) {
    return known;
  }
  return isApiProblem(error) ? problemCopy(error) : null;
}

export function assessmentPageProblemCopy(error: unknown): ProblemCopy {
  const known = knownCopy(problemCodeOf(error), "assessment");
  if (known !== undefined) {
    return known;
  }
  if (!isApiProblem(error)) {
    return UNKNOWN;
  }
  return error.status === 404 ? NOT_FOUND : problemCopy(error);
}

export function isStale(error: unknown): boolean {
  return isApiProblem(error) && error.status === 412;
}

export function readingNotStartedCopy(problem: Pick<Problem, "code" | "detail" | "title">): ProblemCopy {
  return (
    knownCopy(problem.code, "documents") ?? {
      title: "Reading has not started",
      description: problem.detail ?? problem.title,
    }
  );
}
